/**
 * services/AccountRiskService.ts
 * Service for managing account risk configurations with embedded risk profiles
 *
 * AccountRisk entities contain multiple RiskProfile configurations that define
 * risk parameters for different market conditions. During trade entry, users
 * select which profile to apply based on current conditions.
 *
 * Extends BaseService to provide CRUD operations with account-based filtering
 * and individual profile lookup.
 */

import { BaseService } from './BaseService';
import type { AccountRisk, RiskProfile } from '@/types/schema/AccountRisk';
import { isEntityActive } from '@/types/shared/EntityStatus';

/**
 * Service for managing account risk configurations
 *
 * Provides CRUD operations plus account-based filtering and risk profile access.
 * Inherits getAll(), get(), update(), softDelete() from BaseService.
 */
export class AccountRiskService extends BaseService<AccountRisk> {
  constructor() {
    super('account_risks');
  }

  /**
   * Add a new account risk configuration with default values
   *
   * Defaults to status: 'active' and empty riskProfiles array if not provided.
   *
   * @param data - Partial account risk data
   * @returns Promise resolving to the created account risk
   */
  override async add(data: Partial<AccountRisk>): Promise<AccountRisk> {
    return super.add({ status: 'active', riskProfiles: [], ...data });
  }

  /**
   * Get account risk configurations by account ID
   *
   * Filters configurations by the specified account.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @param accountId - The account ID to filter by
   * @returns Promise resolving to array of account risk configurations for this account
   */
  async getByAccountId(accountId: string): Promise<AccountRisk[]> {
    const allRisks = await this.getAll();
    return allRisks.filter(risk => risk.accountId === accountId);
  }

  /**
   * Get active account risk configurations by account ID
   *
   * Filters configurations by account ID and active status.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @param accountId - The account ID to filter by
   * @returns Promise resolving to array of active account risk configurations
   */
  async getActiveByAccountId(accountId: string): Promise<AccountRisk[]> {
    const allRisks = await this.getAll();
    return allRisks.filter(
      risk => risk.accountId === accountId && isEntityActive(risk.status)
    );
  }

  /**
   * Get a specific risk profile from an account risk configuration
   *
   * Fetches the account risk entity and searches its embedded riskProfiles array.
   * Returns undefined if the account risk is not found or the profile ID doesn't exist.
   *
   * @param accountRiskId - The account risk configuration ID
   * @param profileId - The risk profile ID within the configuration
   * @returns Promise resolving to the risk profile, or undefined if not found
   */
  async getRiskProfile(
    accountRiskId: string,
    profileId: string
  ): Promise<RiskProfile | undefined> {
    const accountRisk = await this.get(accountRiskId);
    if (!accountRisk) {
      return undefined;
    }
    return accountRisk.riskProfiles.find(profile => profile.id === profileId);
  }
}

// Export singleton instance
export const accountRiskService = new AccountRiskService();
